import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import DefaultSEO from '../components/DefaultSEO';

const PaymentStatusPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Read transaction and booking parameters from Razorpay or redirect URL
    const bookingIdParam = searchParams.get('bookingId') || '';
    const transactionId = searchParams.get('txnId') || searchParams.get('merchantTransactionId') || searchParams.get('transactionId') || searchParams.get('paymentId') || bookingIdParam;
    
    const [status, setStatus] = useState('pending'); // 'pending' | 'success' | 'failed' | 'error'
    const [loading, setLoading] = useState(true);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        // Prevent back navigation to Razorpay or Checkout
        window.history.pushState(null, null, window.location.href);
        const handlePopState = () => {
            navigate('/', { replace: true });
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [navigate]);

    useEffect(() => {
        if (!transactionId) {
            setStatus('error');
            setErrorMessage('Invalid or missing transaction identifier.');
            setLoading(false);
            return;
        }

        // Mock simulation helper for testing without backend keys
        if (transactionId.startsWith('mock-')) {
            const timer = setTimeout(() => {
                setLoading(false);
                if (transactionId.startsWith('mock-success')) {
                    setStatus('success');
                    setPaymentDetails({
                        bookingId: bookingIdParam || 'B-MOCK12345',
                        transactionId: transactionId,
                        amount: 29500,
                        date: new Date(),
                        paymentMode: 'Razorpay UPI/Cards',
                        hotelName: 'Taj Mahal Palace, Mumbai',
                        roomType: 'Luxury Grande Room',
                        checkInDate: new Date(),
                        checkOutDate: new Date(Date.now() + 86400000)
                    });
                } else {
                    setStatus('failed');
                    setErrorMessage('The mock transaction was declined or failed.');
                }
            }, 2000);
            return () => clearTimeout(timer);
        }

        let isMounted = true;
        let pollCount = 0;
        const maxPolls = 5;
        const pollIntervalMs = 3000;
        let pollTimer;

        // Poll backend API to fetch status of transaction
        const checkPaymentStatus = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
                const cleanApiBase = apiBase.replace(/\/+$/, '');
                const token = localStorage.getItem('auth_token');

                const response = await fetch(`${cleanApiBase}/bookings/status/${transactionId}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': token ? `Bearer ${token}` : ''
                    }
                });

                if (!isMounted) return;

                if (response.ok) {
                    const data = await response.json();
                    
                    if (data.success && data.data) {
                        const txnStatus = data.data.status; // e.g. 'SUCCESS', 'FAILED', 'PENDING'

                        if (txnStatus === 'SUCCESS') {
                            setStatus('success');
                            setPaymentDetails({
                                bookingId: data.data.booking_id || bookingIdParam,
                                transactionId: transactionId,
                                amount: data.data.amount || 0,
                                date: data.data.payment_date ? new Date(data.data.payment_date) : new Date(),
                                paymentMode: data.data.payment_mode || 'Razorpay Gateway',
                                hotelName: data.data.hotel_name || 'Booking Reservation',
                                roomType: data.data.room_type || 'Reserved Suite',
                                checkInDate: data.data.check_in_date,
                                checkOutDate: data.data.check_out_date
                            });
                            setLoading(false);
                            return;
                        } else if (txnStatus === 'FAILED') {
                            setStatus('failed');
                            setErrorMessage(data.data.failure_reason || 'Payment was declined by the bank or gateway.');
                            setLoading(false);
                            return;
                        }
                    }
                }
                
                // If it is pending or backend is still updating, continue polling up to maxPolls
                pollCount++;
                if (pollCount < maxPolls) {
                    pollTimer = setTimeout(checkPaymentStatus, pollIntervalMs);
                } else {
                    setStatus('pending');
                    setLoading(false);
                }
            } catch (err) {
                console.error("Error verifying payment status:", err);
                if (pollCount < maxPolls) {
                    pollCount++;
                    pollTimer = setTimeout(checkPaymentStatus, pollIntervalMs);
                } else {
                    setStatus('error');
                    setErrorMessage('Unable to reach verification servers. Please check your booking history.');
                    setLoading(false);
                }
            }
        };

        checkPaymentStatus();

        return () => {
            isMounted = false;
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [transactionId, bookingIdParam]);

    // Render loading state
    if (loading) {
        return (
            <div className="container min-vh-100 d-flex flex-column align-items-center justify-content-center text-center py-5">
                <DefaultSEO title="Processing Payment | ZoyoTrip" />
                <div className="mb-4 position-relative">
                    <Loader2 size={64} className="text-primary animate-spin" />
                </div>
                <h2 className="fw-bold mb-2">Verifying Payment</h2>
                <p className="text-muted fs-5 mb-0" style={{ maxWidth: '500px' }}>
                    Please do not close this window or click back. We are verifying your transaction status with Razorpay...
                </p>
            </div>
        );
    }

    // Success Screen
    if (status === 'success' && paymentDetails) {
        return (
            <div className="container py-5 min-vh-100 d-flex flex-column align-items-center justify-content-center">
                <DefaultSEO title="Payment Successful | ZoyoTrip" />
                <div className="card border-0 shadow-lg p-4 rounded-4 text-center mb-4" style={{ maxWidth: '600px', width: '100%' }}>
                    <div className="card-body">
                        <div className="mb-4">
                            <CheckCircle size={72} className="text-success mx-auto mb-2" />
                            <h2 className="fw-bold text-dark">
                                {paymentDetails.paymentMode === 'Pay at Hotel' ? 'Booking Confirmed!' : 'Payment Successful!'}
                            </h2>
                            <p className="text-muted">
                                {paymentDetails.paymentMode === 'Pay at Hotel' ? 'Your booking has been confirmed. You can pay at the property.' : 'Your booking has been confirmed successfully.'}
                            </p>
                        </div>

                        <hr className="my-4 text-muted opacity-25" />

                        <div className="text-start bg-light p-4 rounded-3 mb-4">
                            <h6 className="fw-bold text-uppercase text-secondary mb-3 small tracking-wider">Booking Details</h6>
                            
                            <div className="row g-3">
                                <div className="col-sm-6">
                                    <span className="text-muted d-block small">Hotel</span>
                                    <strong className="text-dark">{paymentDetails.hotelName}</strong>
                                </div>
                                <div className="col-sm-6">
                                    <span className="text-muted d-block small">Room Type</span>
                                    <strong className="text-dark">{paymentDetails.roomType}</strong>
                                </div>
                                <div className="col-sm-6">
                                    <span className="text-muted d-block small">Booking ID</span>
                                    <strong className="text-primary">{paymentDetails.bookingId}</strong>
                                </div>
                                <div className="col-sm-6">
                                    <span className="text-muted d-block small">Transaction ID</span>
                                    <span className="text-dark font-monospace small">{paymentDetails.transactionId}</span>
                                </div>
                                <div className="col-sm-6">
                                    <span className="text-muted d-block small">
                                        {paymentDetails.paymentMode === 'Pay at Hotel' ? 'Amount' : 'Amount Paid'}
                                    </span>
                                    <strong className="text-dark fs-5">₹{paymentDetails.amount.toLocaleString('en-IN')}</strong>
                                </div>
                                <div className="col-sm-6">
                                    <span className="text-muted d-block small">Transaction Date</span>
                                    <span className="text-dark">{format(new Date(paymentDetails.date), 'MMM d, yyyy, h:mm a')}</span>
                                </div>
                                {paymentDetails.checkInDate && paymentDetails.checkOutDate && (
                                    <>
                                        <div className="col-sm-6">
                                            <span className="text-muted d-block small">Check-in</span>
                                            <strong className="text-dark">{format(new Date(paymentDetails.checkInDate), 'MMM d, yyyy')}</strong>
                                        </div>
                                        <div className="col-sm-6">
                                            <span className="text-muted d-block small">Check-out</span>
                                            <strong className="text-dark">{format(new Date(paymentDetails.checkOutDate), 'MMM d, yyyy')}</strong>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="d-flex flex-column sm-flex-row gap-3 justify-content-center">
                            <Link to="/account/bookings" className="btn btn-primary px-4 py-3 rounded-pill fw-bold">
                                View My Bookings
                            </Link>
                            <Link to="/" className="btn btn-outline-secondary px-4 py-3 rounded-pill fw-bold">
                                Go to Home
                            </Link>
                        </div>
                    </div>
                </div>
                
                {paymentDetails.paymentMode !== 'Pay at Hotel' && (
                    <div className="d-flex align-items-center gap-2 text-muted">
                        <ShieldCheck size={18} className="text-success" />
                        <span className="small">Secured by Razorpay Payment Gateway</span>
                    </div>
                )}
            </div>
        );
    }

    // Failed Screen
    if (status === 'failed') {
        return (
            <div className="container py-5 min-vh-100 d-flex flex-column align-items-center justify-content-center">
                <DefaultSEO title="Payment Failed | ZoyoTrip" />
                <div className="card border-0 shadow-lg p-4 rounded-4 text-center mb-4" style={{ maxWidth: '500px', width: '100%' }}>
                    <div className="card-body">
                        <div className="mb-4">
                            <XCircle size={72} className="text-danger mx-auto mb-2" />
                            <h2 className="fw-bold text-dark">Payment Failed</h2>
                            <p className="text-muted">We couldn't process your transaction.</p>
                        </div>

                        {errorMessage && (
                            <div className="alert alert-danger border-0 rounded-3 mb-4 py-3 text-start small">
                                <div className="d-flex gap-2">
                                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-danger" />
                                    <span>{errorMessage}</span>
                                </div>
                            </div>
                        )}

                        <p className="text-muted mb-4 small">
                            Don't worry, if your money was debited, it will be refunded automatically to your original payment source within 3-5 business days.
                        </p>

                        <div className="d-flex flex-column gap-2">
                            <button 
                                onClick={() => navigate('/', { replace: true })} 
                                className="btn btn-danger py-3 rounded-pill fw-bold"
                            >
                                Back to Home
                            </button>
                            <Link to="/" className="btn btn-link text-secondary fw-semibold decoration-none">
                                Return to Home
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Pending / Timeout Screen
    if (status === 'pending') {
        return (
            <div className="container py-5 min-vh-100 d-flex flex-column align-items-center justify-content-center">
                <DefaultSEO title="Payment Pending | ZoyoTrip" />
                <div className="card border-0 shadow-lg p-4 rounded-4 text-center mb-4" style={{ maxWidth: '500px', width: '100%' }}>
                    <div className="card-body">
                        <div className="mb-4">
                            <Loader2 size={72} className="text-warning mx-auto mb-2 animate-spin" />
                            <h2 className="fw-bold text-dark">Payment Pending</h2>
                            <p className="text-muted">Razorpay is processing your payment, but we haven't received confirmation yet.</p>
                        </div>

                        <div className="text-start bg-light p-3 rounded-3 mb-4 small">
                            <p className="mb-2"><strong>Transaction ID:</strong> <span className="font-monospace">{transactionId}</span></p>
                            <p className="mb-0">Please do not attempt to pay again. You can check the status in your booking history dashboard in a few minutes.</p>
                        </div>

                        <div className="d-flex flex-column gap-2">
                            <Link to="/account/bookings" className="btn btn-warning py-3 rounded-pill fw-bold text-dark">
                                Check Bookings Status
                            </Link>
                            <Link to="/" className="btn btn-link text-secondary fw-semibold decoration-none">
                                Return to Home
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Generic Error Screen (Invalid Txn)
    return (
        <div className="container py-5 min-vh-100 d-flex flex-column align-items-center justify-content-center">
            <DefaultSEO title="Payment Error | ZoyoTrip" />
            <div className="card border-0 shadow-lg p-4 rounded-4 text-center" style={{ maxWidth: '500px', width: '100%' }}>
                <div className="card-body">
                    <XCircle size={72} className="text-danger mx-auto mb-2" />
                    <h2 className="fw-bold text-dark">Error</h2>
                    <p className="text-muted mb-4">{errorMessage || 'Something went wrong.'}</p>
                    <Link to="/" className="btn btn-primary px-5 py-3 rounded-pill fw-bold">
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PaymentStatusPage;
